import { HconCommonRegExp } from '@/constants/hconRegexConstants';
import moment from 'moment-timezone';

export const HconCommonUtils = {
  // 기존 차세대
  jsonExpr2PropertyObject: <T>(jsonExpr: string): Property<T> => {
    const jsonObj = jsonExpr ? JSON.parse(jsonExpr) : {};
    return {name: jsonObj.name, value: jsonObj.value as T};
  },
  isEmpty: (tobj: any) => {
    if (!tobj) {
      return true;
    } else if (Array.isArray(tobj)) {
      return tobj.length == 0;
    } else if (typeof(tobj) == 'string') {
      return tobj.length == 0;
    } else if (tobj.value !== undefined) {
      if (!tobj.value) {
        return true;
      } else if (Array.isArray(tobj.value)) {
        return tobj.value.length == 0;
      } else if (typeof(tobj.value) == 'string') {
        return tobj.value.length == 0;
      }
    }
    return false;
  },
  isNotEmpty: (tobj: any) => {
    return !cu.isEmpty(tobj);
  },
  getFirst: (arr: any, defaultValue?: any, allocate?: boolean) => {
    let rv;
    if (arr) {
      if (Array.isArray(arr)) {
        if (arr.length == 0) {
          if (allocate) {
            rv  = arr[0]  = defaultValue;
          }
        } else {
          rv  = arr[0];
        }
      } else {
        rv  = arr;
      }
    }
    if (rv === undefined) {
      return defaultValue;
    } else if (rv == null) {
      return defaultValue === undefined ? null : defaultValue;
    } else {
      return rv;
    }
  },
  lpad: (data: any, size: number, filler: string)  => {
    if (!data) {
      data  = '';
    }
    let rv  = '' + data;
    if (cu.isNotEmpty(filler)) {
      while(rv.length < size) {
        rv  = filler + rv;
      }
    }
    return rv;
  },
  rpad: (data: any, size: number, filler: string)  => {
    if (!data) {
      data  = '';
    }
    let rv  = '' + data;
    if (cu.isNotEmpty(filler)) {
      while(rv.length < size) {
        rv  = rv + filler;
      }
    }
    return rv;
  },
  copyProps: (from: any, to: any) => {
    for (const propKey in from) {
      to[propKey] = from[propKey];
    }
  },
  isOnProduct: () => {
    return !import.meta.dev;
  },
  makeQueryString: (data: any, prefix?: string) => {
    const makeParameter = (obj: any, prefix?: string): string => {
      if (obj == null) {
        return '';
      } else if (Array.isArray(obj)) {
        let parts  = [];
        for (let v of obj) {
          const part  = makeParameter(v, prefix);
          if (cu.isNotEmpty(part)) {
            parts.push(part);
          }
        }
        return parts.join('&');
      } else if (typeof(obj) == 'object') {
        let parts  = [];
        for (let key of Object.keys(obj)) {
          const part  = makeParameter(obj[key], prefix ? prefix + '.' + key : key);
          if (cu.isNotEmpty(part)) {
            parts.push(part);
          }
        }
        return parts.join('&');
      } else {
        return encodeURIComponent(prefix ? prefix : 'param') + '=' + encodeURIComponent(obj);
      }
    }
    return makeParameter(data, prefix);
  },
  promiseOf: <T>(result: T) => {
    return new Promise<T>((handleResult)=> handleResult(result));
  },
  path4device: (path: string, isMobile?: boolean) => {
    const paths = path.split('/');
    if (paths.length < 2) {
      return path;
    }
    if (paths[1] != '-device-' && paths[1] != 'pc' && paths[1] != 'mo') {
      return path;
    }
    if (isMobile === undefined) {
      const locpaths  = location.pathname.split('/');
      if (locpaths.length < 2) {
        paths[1]  = paths[1] == '-device-' ? 'pc' : paths[1];
      } else {
        paths[1]  = locpaths[1];
      }
    } else {
      paths[1]  = isMobile ? 'mo' : 'pc';
    }
    return paths.join('/');
  },
  // HCON 추가
  chkTextAreaSpecialChar: (value: string) => {
    const allowedSpecialChars: string[] = ['`', '~', '!', '@', '#', '$', '%', '^', '&', '*'
      , '(', ')', '=', '+', '[', ']', '{', '}', ';', "'", ':', '＼', ',', '.', '/'
      , '<', '>', '?', '♥', '♡', '★', '☆', '●', '○', '■', '□', '◀', '◁', '▶', '▷', '◆', '◇', '※', '☎', '♬'];
    const specialChar = value.match(/[^\p{L}\p{N}\s]/gu);

    // 특수문자 없으면 통과
    if (!specialChar) return true;

    // 허용되지 않는 특수문자가 하나라도 있으면 false
    return specialChar.every(char => allowedSpecialChars.includes(char.trim()));
  },
  format: {
    // 메시지 파라미터 변환
    message: (msg: string, ...args: any): string => {
      return msg.replace(/{([A-Za-z0-9_]+)}/g, (_, key) => {
        return args[Number(key)];
      });
    },
    convertToKey: (code: number) => {
      const convertedcode = String(code).slice(-4);
      return `ALT_HCON_${convertedcode}`;
    },
    // 전화번호 형식 변환 ('-' 형식 변환)
    phone: (target: string) => {
      if (!target) {
        return '';
      }
      if (target.indexOf('-') > -1 ) {
        return target;
      }

      const cleaned = target.replace(/\D/gi, '').slice(0, 11);

      // 대표전화번호(부가서비스번호) 형식 체크
      const matchRps = cleaned.match(/^(15\d{2}|16\d{2}|18\d{2}|19\d{2})(\d{4})$/);
      if (matchRps) {
        return `${matchRps[1]}-${matchRps[2]}`;
      }
      
      // 전화번호 및 유선 전화번호 형식
      const match = cleaned.match(/^(02|0\d{2})(\d+)(\d{4})$/);
      if (!match) return target;

      return `${match[1]}-${match[2]}-${match[3]}`;
      
    },
    // 날짜 형식 변환
    date: (targetDate?: Date | string, format?: string) => {
      if (!targetDate ) {
        return targetDate;
      } else if (typeof(targetDate) === 'string' && String(targetDate).length < 8) {
        return String(targetDate);
      } else if (targetDate instanceof Date && Number.isNaN(targetDate.getTime())) {
        return '';
      }
      const pattern = format ?? 'YYYY-MM-DD';
      const expressions : Record<string, string> = {
        'YYYY': '[1-2]\\d{3}',
        'MM': '[0-1][1-9]',
        'DD': '[0-3][1-9]',
      }
      const regex = new RegExp(pattern.replace(/YYYY|MM|DD/g, match => expressions[match]));
      function getResult(year: string, month : string, day: string ) {
        const replacements : Record<string, string> = {
          'YYYY': year,
          'MM': month,
          'DD': day,
        }
        return pattern.replace(/YYYY|MM|DD/g, match => replacements[match]);
      }
      if (targetDate instanceof Date) {
        const year = String(targetDate.getFullYear());
        const month = String(targetDate.getMonth() + 1).padStart(2, '0');
        const day = String(targetDate.getDate()).padStart(2, '0');
        return getResult(year, month, day);
      }
      const dateString =  (targetDate??'').replace(/\D/gi, "").slice(0, 8); //숫자만 자름
      if (!regex.test(dateString)) {
        const year = dateString.slice(0,4);
        const month = dateString.slice(4,6);
        const day = dateString.slice(6,8);
        return getResult(year, month, day );
      } else {
        return targetDate;
      }
    },
    dateTime: (targetDate?: string) => {
      if (!targetDate || targetDate.length !== 14) return;

      const year = targetDate.slice(0, 4);
      const month = targetDate.slice(4, 6);
      const day = targetDate.slice(6, 8);
      const hour = targetDate.slice(8, 10);
      const minute = targetDate.slice(10, 12);
      const second = targetDate.slice(12, 14);

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    dateTimeNoSec: (targetDate?: string) => {
      if (!targetDate || (targetDate.length !== 14 && targetDate.length !== 12)) return;

      const year = targetDate.slice(0, 4);
      const month = targetDate.slice(4, 6);
      const day = targetDate.slice(6, 8);
      const hour = targetDate.slice(8, 10);
      const minute = targetDate.slice(10, 12);

      return `${year}-${month}-${day} ${hour}:${minute}`;
    },
    todayDateTime: () => {

      const today = new Date();

      const year = today.getFullYear().toString()
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const hour = String(today.getHours()).padStart(2, '0');
      const minute = String(today.getMinutes()).padStart(2, '0');
      const second = String(today.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    todayDate: () => {

      const today = new Date();

      const year = today.getFullYear().toString()
      const month = ((today.getMonth() + 1) < 10) ? '0' + (today.getMonth() + 1).toString() : (today.getMonth() + 1).toString()
      const day = ((today.getDate()) < 10) ? '0' + (today.getDate()).toString() : (today.getDate()).toString()

      return `${year}-${month}-${day}`
    },
    todayDateKor: (isKorFormat?: boolean) => {

      const today = new Date();

      const year = today.getFullYear().toString()
      const month = ((today.getMonth() + 1) < 10) ? '0' + (today.getMonth() + 1).toString() : (today.getMonth() + 1).toString()
      const day = (today.getDate() < 10) ? '0' + today.getDate().toString() : today.getDate().toString()

      if(isKorFormat) {
        return `${year}년 ${month}월 ${day}일`  
      } else {
        return `${year}${month}${day}`
      }
    },
    // 파일 다운로드 시 파일명으로 사용할 현재 시간
    nowDtm: () => {
      const now = new Date();
      const yyyy = now.getFullYear();
      const MM = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');

      const HH = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');

      return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
    },
    // 백엔드에서 파일 다운로드 시 세팅되는 파일명과 통일
    nowDtm_ver2: () => {
      const now = new Date();
      const yyyy = now.getFullYear();
      const MM = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');

      const HH = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');

      return `${yyyy}${MM}${dd}-${HH}${mm}${ss}`;
    },
    dateDiff: (start?: any, end?: any) => {

      const startDate = moment(start,'YYYYMMDD');
      const endDate = moment(end,'YYYYMMDD');

      return endDate.diff(startDate, 'days');
    },
    dateTimeDiff: (start?: any, end?: any) => {

      const startDate = moment(start,'YYYYMMDDHHmmss');
      const endDate = moment(end,'YYYYMMDDHHmmss');
      
      return endDate.diff(startDate, 'seconds');
    },
    expComma: (val?: string | number) => {
      if (!val) {
        return '0';
      }
      const str = String(val);
      // 소수점 분리
      const dotIndex = str.indexOf('.');
      let integerPart = str;

      if (dotIndex !== -1) {
        integerPart = str.slice(0, dotIndex);
      }

      return integerPart
          ? String(Number(integerPart)) // 첫글자 0 치환을 위해 숫자형 변경 후 replace
            .replace(HconCommonRegExp.amd_comma_regex, ",")
          : "0";
    },
    addWon: (val?: string | number) => {
      return HconCommonUtils.format.expComma(val) + "원";
    },
    account: (target: string) => {
      if (!target) {
        return '';
      }
      // 3자리 이하      : 입력값 그대로
      // 4자리~7자리 이하 : 3-나머지
      // 10자리 이하     : 3-4-나머지
      // 11자리 ~ 12자리 : 4-4-나머지
      // 13자리 이상     : 3-6-2-나머지
      const targetLen = target.length;
      if (targetLen <= 3) {
        return target;
      } else if (targetLen > 3 && targetLen <= 7) {
        return target.replace(/(\d{3})(\d+)/, '$1-$2');
      } else if (targetLen > 7 && targetLen <= 10) {
        return target.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
      } else if (targetLen > 11 && targetLen <= 12) {
        return target.replace(/(\d{4})(\d{4})(\d+)/, '$1-$2-$3');
      } else {
        return target.replace(/(\d{3})(\d{6})(\d{2})(\d+)/, '$1-$2-$3-$4');
      }
    },
    // 사업자번호 3-2-5 형식 변환
    bzrn: (target: string) => {
      if (!target) {
        return '';
      }

      return target.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
    },
  }
}
